import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import {
  REVISION_DETAIL_FIELD_SECTIONS,
  getRevisionDiagnosisText,
  validateMarkErrorReason,
} from "./revisionRapidaSaludDetailUtils";
import {
  buildRevisionRapidaSaludPath,
  buildRevisionRapidaSaludRecordPath,
  buildRevisionRapidaSaludRecordsPath,
} from "./revisionRapidaSaludUtils";
import { getValidConvocatoriaContext } from "./revisionRapidaSaludJornadaContext";
import useRevisionRegistroDraft from "./useRevisionRegistroDraft";

const Section = ({ title, subtitle, children }) => (
  <Box component="section" sx={{ py: 2.5 }}>
    <Typography component="h2" variant="h5" sx={{ fontWeight: 900, mb: subtitle ? 0.5 : 2 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Box>
);

const ReadOnlyField = ({ label, value, helperText }) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    value={value ?? ""}
    placeholder="Sin dato"
    helperText={helperText}
    InputLabelProps={{ shrink: true }}
    InputProps={{ readOnly: true }}
    inputProps={{ "aria-readonly": "true" }}
    sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
  />
);

const ReadOnlyTreatment = ({ label, value }) => (
  <Box
    sx={{
      p: 1.5,
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      bgcolor: "background.paper",
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography>{value}</Typography>
  </Box>
);

const renderListValue = (value) => {
  if (!Array.isArray(value) || value.length === 0) return "";
  return value.filter((item) => item !== null && item !== undefined && item !== "").join(", ");
};

const RevisionRegistroEditor = ({
  convocatoriaId: convocatoriaIdProp,
  registroId: registroIdProp,
  displayMode = "full",
  onDirtyChange,
  onRequestClose,
  onMutationSuccess,
  onRequestFullDetail,
}) => {
  const routeParams = useParams();
  const convocatoriaId = convocatoriaIdProp ?? routeParams.convocatoriaId;
  const registroId = registroIdProp ?? routeParams.registroId;
  const location = useLocation();
  const navigate = useNavigate();
  const inline = displayMode === "inline";
  const [markErrorOpen, setMarkErrorOpen] = useState(false);
  const [markErrorReason, setMarkErrorReason] = useState("");
  const [markErrorValidation, setMarkErrorValidation] = useState("");
  const [unsavedDialog, setUnsavedDialog] = useState(null);
  const [snackbar, setSnackbar] = useState("");

  const handleMutationSuccess = (result) => {
    setMarkErrorOpen(false);
    setMarkErrorReason("");
    setSnackbar(result.message);
    onMutationSuccess?.(result);
  };

  const {
    serverSnapshot,
    draft,
    initialLoading,
    refreshing,
    saving,
    error,
    fieldErrors,
    stateDialog,
    setStateDialog,
    dirty,
    status,
    source,
    legacy,
    fieldsEditable,
    diagnosesEditable,
    canSave,
    canVerify,
    canMarkError,
    loadDetail,
    setDraftField,
    setOrderedSlot,
    resetDraft,
    executeMutation,
  } = useRevisionRegistroDraft({
    convocatoriaId,
    registroId,
    onDirtyChange,
    onMutationSuccess: handleMutationSuccess,
  });

  const provenance = serverSnapshot?.trace?.lastModified?.provenance;
  const provenanceLabel = provenance === "PROVENANCE_VERIFIED"
    ? "Origen verificado"
    : provenance === "PROVENANCE_LEGACY_INFERRED" ? "Origen histórico" : "";

  const updateDraftField = (section, definition) => (event) => {
    setDraftField(section, definition, event.target.value);
  };

  const updateOrderedSlot = (key, index) => (event) => {
    setOrderedSlot(key, index, event.target.value);
  };

  const handleMarkError = () => {
    const validation = validateMarkErrorReason(markErrorReason);
    setMarkErrorValidation(validation);
    if (validation) return;
    executeMutation("mark_error", { reason: markErrorReason.trim() });
  };

  const listPath = buildRevisionRapidaSaludRecordsPath(convocatoriaId);
  const dashboardPath = buildRevisionRapidaSaludPath(convocatoriaId);
  const convocatoriaContext = getValidConvocatoriaContext(
    location.state?.convocatoriaContext,
    convocatoriaId
  );
  const navigateWithListState = (target) =>
    navigate(target, {
      state: {
        listState: location.state?.listState,
        context: location.state?.context,
        convocatoriaContext,
      },
    });

  const requestNavigation = (target) => {
    if (dirty) setUnsavedDialog({ type: "navigate", target });
    else navigateWithListState(target);
  };

  const requestClose = () => {
    if (onRequestClose) onRequestClose({ dirty });
    else requestNavigation(listPath);
  };

  const requestFullDetail = () => {
    const target = buildRevisionRapidaSaludRecordPath(convocatoriaId, registroId);
    if (onRequestFullDetail) onRequestFullDetail({ dirty, target });
    else requestNavigation(target);
  };

  const requestRefresh = () => {
    if (dirty) setUnsavedDialog({ type: "refresh" });
    else loadDetail();
  };

  const confirmDiscard = () => {
    const pending = unsavedDialog;
    setUnsavedDialog(null);
    if (pending?.type === "refresh") loadDetail();
    if (pending?.type === "navigate") navigateWithListState(pending.target);
  };

  if (initialLoading) {
    const LoadingRoot = inline ? Box : Container;
    return (
      <LoadingRoot maxWidth={inline ? undefined : "lg"} sx={{ py: 6 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={260}>
          <CircularProgress aria-label="Cargando registro" />
        </Box>
      </LoadingRoot>
    );
  }

  if (!serverSnapshot || !draft) {
    const ErrorRoot = inline ? Box : Container;
    return (
      <ErrorRoot maxWidth={inline ? undefined : "lg"} sx={{ p: inline ? 2 : undefined, py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || "No fue posible cargar el registro."}</Alert>
        <Button startIcon={<ArrowBackOutlinedIcon />} onClick={requestClose}>
          {inline ? "Cerrar fila" : "Volver a registros"}
        </Button>
      </ErrorRoot>
    );
  }

  const readOnlyBySection = {
    demographics: [
      ["Grupo étnico", serverSnapshot.fields?.demographics?.grupoEtnicoId],
      ["Nivel educativo", serverSnapshot.fields?.demographics?.nivelEducativoId],
      ["Discapacidad", serverSnapshot.fields?.demographics?.discapacidadId],
      ["Movilidad", serverSnapshot.fields?.demographics?.movilidadId],
    ],
    evaluation: [
      ["Factores de riesgo", renderListValue(serverSnapshot.fields?.evaluation?.factorRiesgoIds)],
    ],
    clinical: [
      ["Médico", serverSnapshot.fields?.clinical?.medico?.nombre],
      ["Clasificación", serverSnapshot.fields?.clinical?.clasificacion],
      ["Estado de atención", serverSnapshot.fields?.clinical?.estadoAtencion],
      ["Patologías legacy", renderListValue(serverSnapshot.fields?.clinical?.legacyPathologyIds)],
    ],
  };

  const Root = inline ? Box : Container;

  return (
    <Root
      maxWidth={inline ? undefined : "lg"}
      data-testid={inline ? "revision-inline-editor" : "revision-full-editor"}
      sx={inline
        ? { p: { xs: 1.5, md: 2.5 }, bgcolor: "background.default", borderTop: 1, borderBottom: 1, borderColor: "divider" }
        : { py: { xs: 2, md: 3 }, pb: 8 }}
    >
      {!inline && <Breadcrumbs aria-label="Navegacion" sx={{ mb: 2 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => requestNavigation("/transcripcion")}>
          Transcripción
        </Link>
        <Link component="button" underline="hover" color="inherit" onClick={() => requestNavigation(dashboardPath)}>
          Revisión de datos
        </Link>
        <Link component="button" underline="hover" color="inherit" onClick={() => requestNavigation(listPath)}>
          Registros
        </Link>
        <Typography color="text.primary">Ficha</Typography>
      </Breadcrumbs>}

      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={2}>
        <Box minWidth={0}>
          <Typography component={inline ? "h3" : "h1"} variant={inline ? "h4" : "h2"} sx={{ fontSize: inline ? undefined : { xs: "1.8rem", md: "2.35rem" }, fontWeight: 900 }}>
            Revisión de registro
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Ficha #{serverSnapshot.id} · {serverSnapshot.fields?.identity?.codigoBeneficiario || "Sin código"}
          </Typography>
        </Box>
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Chip label={legacy ? "LEGACY" : "V2 · Solo lectura"} color={legacy ? "default" : "info"} variant="outlined" />
          <Chip label={status.label} color={status.color} />
          <Chip label={`Versión ${serverSnapshot.version}`} variant="outlined" />
          {provenanceLabel && <Chip label={provenanceLabel} size="small" variant="outlined" />}
          <Button startIcon={<RefreshOutlinedIcon />} onClick={requestRefresh} disabled={saving || refreshing}>
            Actualizar
          </Button>
          {inline && (
            <Button startIcon={<OpenInNewOutlinedIcon />} onClick={requestFullDetail} disabled={saving}>
              Abrir detalle completo
            </Button>
          )}
        </Stack>
      </Stack>

      {source === "V2" && <Alert severity="info" sx={{ mb: 2 }}>Este registro V2 está disponible solo para consulta.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {refreshing && <Alert severity="info" sx={{ mb: 2 }}>Actualizando información...</Alert>}

      <Paper variant="outlined" sx={{ px: { xs: 2, md: 3 }, borderRadius: 1 }}>
        {REVISION_DETAIL_FIELD_SECTIONS.map((section, sectionIndex) => (
          <React.Fragment key={section.key}>
            {sectionIndex > 0 && <Divider />}
            <Section title={section.title}>
              <Grid container spacing={2}>
                {section.fields.map((field) => {
                  const fieldKey = `${section.key}.${field.name}`;
                  const fieldEditable = fieldsEditable && !field.protected;
                  return (
                    <Grid item xs={12} sm={field.multiline ? 12 : 6} md={field.multiline ? 12 : 4} key={fieldKey}>
                      <TextField
                        fullWidth
                        size="small"
                        type={field.type || "text"}
                        label={field.label}
                        value={draft.fields?.[section.key]?.[field.name] ?? ""}
                        onChange={updateDraftField(section.key, field)}
                        multiline={field.multiline}
                        minRows={field.multiline ? 2 : undefined}
                        error={Boolean(fieldErrors[fieldKey])}
                        helperText={fieldErrors[fieldKey] || (!fieldEditable
                          ? field.protected ? "Dato definido por la convocatoria" : "Solo lectura"
                          : " ")}
                        InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
                        InputProps={{ readOnly: !fieldEditable }}
                        inputProps={{ maxLength: field.maxLength, "aria-readonly": String(!fieldEditable) }}
                        sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
                      />
                    </Grid>
                  );
                })}
                {(readOnlyBySection[section.key] || []).map(([label, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={label}>
                    <ReadOnlyField label={label} value={value} helperText="Catálogo disponible solo para consulta en F3" />
                  </Grid>
                ))}
              </Grid>
            </Section>
          </React.Fragment>
        ))}

        <Divider />
        <Section title="Motivos de consulta" subtitle="Se conservan las cinco posiciones del registro original.">
          <Grid container spacing={2}>
            {draft.consultationReasons.map((value, index) => (
              <Grid item xs={12} md={6} key={`reason-${index + 1}`}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Motivo ${index + 1}`}
                  value={value}
                  onChange={updateOrderedSlot("consultationReasons", index)}
                  InputProps={{ readOnly: !fieldsEditable }}
                  inputProps={{ maxLength: 255, "aria-readonly": String(!fieldsEditable) }}
                  sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
                  error={Boolean(fieldErrors[`consultationReasons.${index}`])}
                  helperText={fieldErrors[`consultationReasons.${index}`] || (!fieldsEditable ? "Solo lectura" : " ")}
                />
              </Grid>
            ))}
          </Grid>
        </Section>

        <Divider />
        <Section title="Diagnósticos" subtitle="Diagnósticos legacy conservados por posición.">
          <Grid container spacing={2}>
            {(source === "V2" ? serverSnapshot.diagnoses || [] : draft.diagnoses).map((value, index) => (
              <Grid item xs={12} md={6} key={`diagnosis-${index + 1}`}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Diagnóstico ${index + 1}`}
                  value={source === "V2" ? getRevisionDiagnosisText(value) : value}
                  onChange={updateOrderedSlot("diagnoses", index)}
                  InputProps={{ readOnly: !diagnosesEditable }}
                  inputProps={{ maxLength: 255, "aria-readonly": String(!diagnosesEditable) }}
                  sx={{ "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
                  error={Boolean(fieldErrors[`diagnoses.${index}`])}
                  helperText={fieldErrors[`diagnoses.${index}`] || (!diagnosesEditable ? "Solo lectura" : " ")}
                />
              </Grid>
            ))}
          </Grid>
        </Section>

        <Divider />
        <Section
          title="Tratamientos"
          subtitle="Los tratamientos están disponibles solo para consulta en esta versión."
        >
          {serverSnapshot.treatments?.length ? (
            <Stack spacing={1}>
              {serverSnapshot.treatments.map((treatment, index) => (
                <ReadOnlyTreatment
                  key={`${treatment.medicationId || "treatment"}-${index}`}
                  label={`Tratamiento ${index + 1}`}
                  value={treatment.description || `Medicamento #${treatment.medicationId}`}
                />
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">No hay tratamientos registrados.</Typography>
          )}
        </Section>

        <Divider />
        <Section title="Acciones">
          <Stack direction={{ xs: "column", sm: "row" }} sx={{ gap: 1, flexWrap: "wrap" }}>
            <Button startIcon={<ArrowBackOutlinedIcon />} onClick={requestClose} disabled={saving}>
              {inline ? "Cerrar fila" : "Volver a registros"}
            </Button>
            {dirty && legacy && (
              <Button variant="outlined" onClick={resetDraft} disabled={saving}>
                Cancelar cambios
              </Button>
            )}
            {canMarkError && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<ErrorOutlineOutlinedIcon />}
                onClick={() => setMarkErrorOpen(true)}
                disabled={saving}
              >
                Marcar error
              </Button>
            )}
            {canSave && (
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                onClick={() => executeMutation("save")}
                disabled={saving || !dirty}
              >
                Guardar
              </Button>
            )}
            {canVerify && (
              <Button
                variant="contained"
                startIcon={<VerifiedOutlinedIcon />}
                onClick={() => executeMutation("save_and_verify")}
                disabled={saving}
              >
                Guardar y verificar
              </Button>
            )}
          </Stack>
        </Section>
      </Paper>

      <Dialog open={markErrorOpen} onClose={() => !saving && setMarkErrorOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Marcar registro con error</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Indica un motivo operativo. No incluyas información clínica adicional.
          </Typography>
          {dirty && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Los cambios clínicos sin guardar no se incluirán al marcar el registro con error.
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Motivo"
            value={markErrorReason}
            onChange={(event) => {
              setMarkErrorReason(event.target.value.slice(0, 500));
              setMarkErrorValidation("");
            }}
            error={Boolean(markErrorValidation)}
            helperText={markErrorValidation || `${markErrorReason.length}/500`}
            inputProps={{ maxLength: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkErrorOpen(false)} disabled={saving}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleMarkError} disabled={saving}>
            Marcar error
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(stateDialog)} onClose={() => setStateDialog(null)}>
        <DialogTitle>{stateDialog?.code === "VERSION_CONFLICT" ? "Registro actualizado" : "Estado no disponible"}</DialogTitle>
        <DialogContent>
          <Typography>{stateDialog?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStateDialog(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => {
              setStateDialog(null);
              loadDetail();
            }}
          >
            Recargar información
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(unsavedDialog)} onClose={() => setUnsavedDialog(null)}>
        <DialogTitle>Tienes cambios sin guardar</DialogTitle>
        <DialogContent>
          <Typography>Al continuar se descartarán los cambios realizados en esta ficha.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnsavedDialog(null)}>Seguir editando</Button>
          <Button color="error" variant="contained" onClick={confirmDiscard}>Descartar cambios</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3500}
        onClose={() => setSnackbar("")}
        message={snackbar}
      />
    </Root>
  );
};

export default RevisionRegistroEditor;
